import { ReactElement, useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { materialTypeOptions } from '@domainConstants'
import { renameSWxxToCxxIfApplicable } from '@domainUtils'
import { materialQueryFromMaterialType } from '@editorUtils'
import { Info } from '@mui/icons-material'
import { Alert, Stack } from '@mui/material'
import { usageClassOptions } from '@editor/constants'
import { SelectLoadable, Select, CentimeterField } from '@ui/forms'
import AutocompleteLoadable from '@ui/forms/AutocompleteLoadable'
import { getSteelCSShapes } from '@queries'
import { defaultSteelCSShape } from '../../schema'

interface Props {
  elementType: ElementTypes
  isDisabled: boolean
  alert?: string
  /** Path of the cross-section object in the form. Defaults to a single `crossSection` field. */
  name?: string
}

const FormFields = ({
  elementType,
  isDisabled,
  alert,
  name = 'crossSection',
}: Props): ReactElement => {
  const { setValue } = useFormContext()
  const materialType = useWatch({ name: `${name}.materialType` })
  const steelShape = useWatch({ name: `${name}.steelShape` })

  useEffect(() => {
    if (materialType === 'steelMaterial' && (!steelShape || typeof steelShape === 'string')) {
      setValue(`${name}.steelShape`, defaultSteelCSShape, {
        shouldDirty: true,
        shouldTouch: true,
      })
    }
  }, [materialType, name, setValue, steelShape])

  return (
    <Stack direction="column" spacing={1} display="flex" flex={1}>
      <Stack direction="row" spacing={1}>
        <Select
          label="Materialtyp"
          name={`${name}.materialType`}
          options={materialTypeOptions}
          data-cy="crosssection-material-type"
          disabled={isDisabled}
          size="small"
          sx={{
            '& .MuiSelect-select': {
              padding: '4px 8px',
            },
          }}
        />
        <SelectLoadable<{ [key: string]: unknown }, Material>
          label="Material"
          name={`${name}.material`}
          placeholder="Material auswählen"
          query={materialQueryFromMaterialType(materialType)}
          renderOptions={(data: Material[] = []) =>
            data.map(material => ({
              value: material,
              label: renameSWxxToCxxIfApplicable(material.identifier),
            }))
          }
          data-cy="crosssection-material"
          disabled={isDisabled}
        />
      </Stack>

      {(materialType === 'softwoodMaterial' ||
        materialType === 'glulamMaterial' ||
        materialType === 'lvlMaterial') && (
        <>
          <Stack direction="row" spacing={1}>
            <CentimeterField
              tooltip={elementType === 'lintels' ? { text: 'Bestimmt Schichtdicke' } : undefined}
              label="QS-Breite"
              name={`${name}.shape.width`}
              data-cy="crosssection-width-input"
              disabled={isDisabled}
              twoDecimalPlaces
            />
            <CentimeterField
              tooltip={elementType === 'rips' ? { text: 'Bestimmt Schichtdicke' } : undefined}
              label="QS-Höhe"
              name={`${name}.shape.height`}
              data-cy="crosssection-height-input"
              disabled={isDisabled}
              twoDecimalPlaces
            />
            <Select
              label="Nutzungsklasse"
              name={`${name}.usage_class`}
              options={usageClassOptions}
              data-cy="crosssection-usage-class-input"
              disabled={isDisabled}
            />
          </Stack>
        </>
      )}
      {materialType === 'steelMaterial' && (
        <Stack direction="row" spacing={1} flex={1}>
          <AutocompleteLoadable
            label="Stahl Querschnitt"
            name={`${name}.steelShape`}
            placeholder="Querschnitt auswählen"
            query={getSteelCSShapes}
            renderOptions={(data: SteelCSShape[] = []) =>
              data.map(csShape => ({ value: csShape, label: csShape.profile }))
            }
            data-cy="crosssection-steel-shape-select"
            disabled={isDisabled}
          />
        </Stack>
      )}

      {alert && (
        <Alert
          icon={<Info fontSize="inherit" />}
          severity="info"
          sx={{ paddingX: 1, paddingY: 0.5 }}
        >
          {alert}
        </Alert>
      )}
    </Stack>
  )
}

export { FormFields, renameSWxxToCxxIfApplicable as remapMaterialIdentifier }
